import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatus } from '../../../services/api';
import { privacyApi, PrivacySettings } from '../../../services/privacyApi';
import { logger } from '../../../utils/logger';
import { LockIcon } from './Icons';

interface ConsentTrackingCardProps {
    userStatus: UserStatus;
    onSettingsChange?: () => void;
}

export default function ConsentTrackingCard({ userStatus, onSettingsChange }: ConsentTrackingCardProps) {
    const { t } = useTranslation();
    
    const [settings, setSettings] = useState<PrivacySettings>({
        marketing: false,
        analytics: false,
        affiliates: true
    });
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    // Sync with userStatus when it changes
    useEffect(() => {
        const privacy = userStatus.settings?.privacy;
        if (privacy) {
            setSettings({
                marketing: privacy.marketing ?? false,
                analytics: privacy.analytics ?? false,
                affiliates: privacy.affiliates ?? true
            });
        }
    }, [userStatus.settings?.privacy]);

    const handleToggle = async (key: keyof PrivacySettings) => {
        const prevSettings = { ...settings };
        const nextSettings = { ...settings, [key]: !settings[key] };
        
        // Optimistic update
        setSettings(nextSettings);
        setLoading(true);
        setSaveStatus('saving');
        
        try {
            console.log('[Privacy] Saving consent settings:', nextSettings);
            await privacyApi.updateConsent(nextSettings, 'v1.4');
            console.log('[Privacy] Settings saved successfully');
            setSaveStatus('saved');
            // Refresh parent status to ensure sync
            onSettingsChange?.();
            // Clear saved status after 2 seconds
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('[Privacy] Failed to save settings:', err);
            logger.error('Failed to update consent', err);
            // Revert on error
            setSettings(prevSettings);
            setSaveStatus('error');
            alert(t('privacy.failed_save'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="privacy-card">
            <div className="card-header">
                <div className="header-icon lock">
                    <LockIcon size={20} />
                </div>
                <h3>{t('privacy.consent_tracking')}</h3>
                {saveStatus === 'saving' && <span className="save-indicator saving">...</span>}
                {saveStatus === 'saved' && <span className="save-indicator saved">✓</span>}
                {saveStatus === 'error' && <span className="save-indicator error">✗</span>}
            </div>

            <div className="consent-sections">
                <div className="consent-group">
                    <div className="group-header">
                        <h4>{t('privacy.strictly_necessary')}</h4>
                        <span className="badge-required">{t('privacy.essential')}</span>
                    </div>
                    <p className="consent-desc">{t('privacy.necessary_desc')}</p>
                </div>

                <div className="consent-group optional">
                    <div className="group-header">
                        <h4>{t('privacy.optional_preferences')}</h4>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <strong>{t('privacy.marketing_updates')}</strong>
                            <span>{t('privacy.marketing_desc')}</span>
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={settings.marketing}
                                onChange={() => handleToggle('marketing')}
                                disabled={loading}
                            />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    <div className="toggle-item">
                        <div className="toggle-info">
                            <strong>{t('privacy.usage_analytics')}</strong>
                            <span>{t('privacy.analytics_desc')}</span>
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={settings.analytics}
                                onChange={() => handleToggle('analytics')}
                                disabled={loading}
                            />
                            <span className="slider round"></span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}
